<template>
    <div class="flex flex-col h-full" :style="fromReatlas ? 'max-width: 700px' : 'max-width: 100%;'">
        <div class="w-full comp-title-bar bg-gradient-to-b from-green-600 to-green-700 py-2" :class="{ 'cursor-move': isModal }">
            <div class="container flex items-center justify-between w-full" :class="{ 'px-5': isModal || fromReatlas, 'mx-auto': ! fromReatlas }">
                <div class="w-full flex items-center">
                    <div class="group flex flex-1 flex-col text-white leading-normal" :class="{ 'hover:text-blue-100': ! isModal && ! fromReatlas }">
                        <div v-if="! isModal && ! fromReatlas" @click="showComps" class="flex cursor-pointer">
                            <div>
                                <div v-if="! isNegotiationMode" class="text-xl font-semibold uppercase">{{ comp.building.address_line_1 }}</div>
                                <div v-else class="text-xl font-semibold uppercase">Alternative Site</div>
                                <div class="text-sm uppercase">{{ comp.building.city }}, {{ comp.building.state }}</div>
                            </div>
                            <div class="flex">
                                <inline-svg name="chevron-down" classes="fill-current text-white group-hover:text-blue-100 h-5 w-5 ml-3 cursor-pointer"></inline-svg>
                            </div>
                        </div>
                        <div v-else>
                            <div v-if="! isNegotiationMode" class="font-semibold uppercase" :class="isModal ? 'text-3xl' : 'text-xl'">{{ comp.building.address_line_1 | truncate(12) }}</div>
                            <div v-else class="font-semibold uppercase" :class="isModal ? 'text-3xl' : 'text-xl'">Alternative Site</div>
                            <div class="uppercase" :class="isModal ? 'text-base' : 'text-sm'">{{ comp.building.city }}, {{ comp.building.state }}</div>
                        </div>
                    </div>
                    <div class="flex items-center space-x-4">
                        <a v-if="fromReatlas" :href="comp.view_url" target="_blank" class="btn btn-small btn-green">
                            Go to Comp Page
                        </a>

                        <a v-if="! fromReatlas" :href="`/projects/${project.id}`" class="flex items-center gap-x-2 btn btn-small btn-green">
                            <inline-svg name="chevron-left" classes="w-5 h-5 text-white fill-current" />
                            Back to Project
                        </a>

                        <div class="py-1 px-2 flex items-center gap-x-2 bg-white border border-gray-300 rounded-md">
                            <div v-if="$gate.isAdmin()" :title="researchStatusTitle">
                                <v-popover trigger="click" offset="10" ref="popover">
                                    <inline-svg
                                        :name="comp.research_status"
                                        classes="fill-current w-9 h-9 mt-2 bg-white text-blue-500 hover:text-blue-600 border border-white rounded-full cursor-pointer">
                                    </inline-svg>

                                    <template slot="popover">
                                        <p class="w-10/12 text-center mt-4 mx-auto">Change the <strong>Research Status</strong> by clicking below</p>
                                        <div class="leading-normal max-w-sm p-6">
                                            <div
                                                v-for="(status, statusIndex) in researchStatuses" :key="statusIndex"
                                                class="flex items-center p-4 mb-4 border border-gray-300 hover:bg-gray-200 cursor-pointer rounded"
                                                :class="comp.research_status == status.slug ? 'bg-yellow-100' : 'bg-white'"
                                                @click="changeResearchStatus(status.slug)"
                                            >
                                                <inline-svg :name="status.slug"></inline-svg>
                                                <span class="ml-2">{{ status.label }}</span>
                                            </div>
                                        </div>
                                    </template>
                                </v-popover>
                            </div>
                            <div v-else :title="researchStatusTitle">
                                <inline-svg :name="comp.research_status" classes="fill-current w-9 h-9 bg-white text-blue-500 border border-white rounded-full"></inline-svg>
                            </div>

                            <div @click="toggleFavorite()">
                                <inline-svg
                                    name="favorite"
                                    classes="w-10 h-10 cursor-pointer"
                                    :class="comp.is_favorite ? 'text-red-600 hover:text-red-700' : 'text-gray-300 hover:text-red-600'">
                                </inline-svg>
                            </div>

                            <div @click="toggleRejected()">
                                <inline-svg                            
                                    name="rejected"
                                    classes="w-10 h-10 cursor-pointer"
                                    :class="comp.is_rejected ? 'text-red-600 hover:text-red-700' : 'text-gray-300 hover:text-red-600'">
                                </inline-svg>
                            </div>

                            <div
                                class="ml-5 flex items-center justify-center w-4 h-4 p-5 rounded-full shadow-lg font-black text-base text-white"
                                :class="comp.score_color">
                                {{ comp.score }}
                            </div>
                        </div>

                        <div v-if="isModal || fromReatlas" @click="$emit('reatlas-close-right-panel')">
                            <inline-svg name="close" classes="fill-current text-white hover:text-gray-300 h-6 w-6 ml-5 cursor-pointer"></inline-svg>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div class="flex-1 overflow-y-auto" :class="{ 'px-5': isModal || fromReatlas }">
            <div class="py-5 mx-auto container">
                <a name="pictures"></a>
                <div class="flex flex-col">           
                    <div class="flex" :class="{ 'mb-5': ! isModal }">
                        <div class="flex-1 mr-4 overflow-hidden">
                            <media-gallery
                                :documentable-id="comp.building.id"
                                documentable-type="App\Building"
                                :media="comp.building.media"
                                :height="500"
                                :object-cover="! fromReatlas">
                            </media-gallery>
                        </div>
                        <div v-if="! fromReatlas" class="w-2/5 shadow-md">
                            <map-component
                                :latitude="comp.building.latitude"
                                :longitude="comp.building.longitude"
                                :zoom="6"
                            ></map-component>
                        </div>
                        <div v-if="fromReatlas">
                            <h2 class="text-sm font-bold uppercase">
                                Quick Facts
                            </h2>

                            <table class="mt-3">
                                <tbody>
                                    <tr>
                                        <td class="px-3 font-semibold">Property Type</td>
                                        <td>{{ comp.building.type.type }}</td>
                                    </tr>
                                    <tr>
                                        <td class="px-3 font-semibold">Price PSF</td>
                                        <td data-precision="0" :data-originalvalue="comp.ask_price" class="is-currency">{{ formatCurrency(comp.ask_price, 2) }}</td>
                                    </tr>
                                    <tr>
                                        <td class="px-3 font-semibold">Avail. Sq. Ft.</td>
                                        <td>{{ comp.building.available_square_footage | number }}</td>
                                    </tr>
                                    <tr>
                                        <td class="px-3 font-semibold">Total Sq. Ft.</td>
                                        <td>{{ comp.building.total_square_footage | number }}</td>
                                    </tr>
                                    <tr>
                                        <td class="px-3 font-semibold">Class</td>
                                        <td>{{ comp.building.class }}</td>
                                    </tr>
                                    <tr>
                                        <td class="px-3 font-semibold">Floors</td>
                                        <td>{{ comp.building.floors }}</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>

                    <div class="mt-3">
                        <div class="border-b border-gray-200">
                            <div v-if="isModal || fromReatlas" class="mt-0 mb-3 flex items-center">
                                <span class="text-gray-700 font-medium">Viewing Section</span>
                                <select v-model="selectedTab" class="ml-2 mt-0 form-select form-select-blue max-w-fit">
                                    <option v-for="(tab, slug) in enabledTabs" :key="slug"  :value="slug">{{ tab.label }}</option>
                                </select>
                            </div>
                            <nav v-if="! fromReatlas" class="-mb-px flex space-x-2" aria-label="Tabs">
                                <button
                                    v-for="(tab, slug) in enabledTabs" :key="slug"
                                    @click="selectedTab = slug"
                                    class="py-2 px-3 border border-gray-200 flex items-center whitespace-nowrap rounded-t-lg text-sm"
                                    :class="{ 'bg-gray-50 text-gray-500 font-medium hover:border-gray-200 hover:text-gray-700': slug != selectedTab, 'bg-gradient-to-br from-green-600 to-green-700 font-bold text-white': slug == selectedTab }">
                                        {{ tab.label }}
                                    <span
                                        v-if="tab.showCount"
                                        class="py-0.5 px-2.5 ml-3 hidden rounded-full text-xs font-medium md:inline-block"
                                        :class="{ 'bg-gray-200 text-gray-900': slug != selectedTab, 'bg-green-800 text-white': slug == selectedTab }">
                                            {{ tab.count }}
                                    </span>
                                </button>
                          </nav>
                        </div>
                    </div>

                    <div class="px-5 mb-6 border-l border-r border-b">
                        <div class="mt-6">
                            <score-comp
                                v-if="selectedTab == 'score'"
                                :project-id="project.id"
                                :scorable-id="comp.id"
                                scorable-type="App\ProjectComp"
                                :data="project.ksd_scenarios"
                                :is-modal="isModal"
                                @comp-score-updated="updateCompScore($event)"
                                :show-chart="! fromReatlas"
                                :from-reatlas="fromReatlas">
                            </score-comp>

                            <building-fields v-if="selectedTab == 'building'" :building-data="comp.building" :from-reatlas="fromReatlas"></building-fields>
        
                            <units
                                v-if="selectedTab == 'units'"
                                :from-reatlas="fromReatlas"
                                show-edit-actions>
                            </units>

                            <documents
                                v-if="selectedTab == 'documents'"
                                :document-data="comp.building.documents"
                                :documentable-id="comp.building_id"
                                documentable-type="App\Building"
                                :from-reatlas="fromReatlas"
                            ></documents>

                            <contacts
                                v-if="selectedTab == 'contacts'"
                                :contact-data="comp.building.contacts"
                            ></contacts>

                            <comments
                                v-if="selectedTab == 'comments'"
                                :data="comp.comments"
                                :document-data="comp.building.documents"
                                :commentable-id="comp.id"
                                commentable-type="App\ProjectComp"
                            ></comments>

                            <effective-rent-calculator
                                v-if="selectedTab == 'effective_rent_calculator'"
                                :project-id="comp.project_id"
                                :data="comp.effective_rent_calculation"
                                :from-reatlas="fromReatlas">
                            </effective-rent-calculator>

                            <lease-vs-purchase-calculator
                                v-if="selectedTab == 'lease_vs_purchase_calculator'"
                                :comp-data="comp"
                                :calculation-data="comp.lease_vs_purchase_calculation">
                            </lease-vs-purchase-calculator>


                            <div v-if="$gate.isAdmin()" class="my-10">
                                <change-log v-if="selectedTab == 'changelog'" :data="comp.audits"></change-log>
                            </div>
                        </div>
                    </div>

                    <notifications classes="notification" position="bottom right"></notifications>

                    <modal @opened="$eventHub.$emit('convert-currency')" name="comp-table" width="60%" height="auto" click-to-close scrollable adaptive>
                        <div class="p-8">
                            <comp-table
                                :project-id="project.id"
                                :comp-data="project.comps">
                            </comp-table>
                        </div>
                    </modal>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import { mapState, mapGetters } from 'vuex';
    import moment from 'moment';

    export default {
        name: 'ReoptComp',

        props: {
            projectData: {
                type: Object,
                required: true
            },

            compData: {
                type: Object,
                required: true
            },

            openTab: {
                type: String,
                default: 'score'
            },

            isModal: {
                type: Boolean,
                default: false
            },

            fromReatlas: {
                type: Boolean,
                default: false
            }
        },

        data() {
            return { 
                selectedTab: this.openTab,

                researchStatuses: [
                    {
                        slug: 'not-researched',
                        label: 'Not Researched'
                    },
                    {
                        slug: 'researching',
                        label: 'Researching'
                    },
                    {
                        slug: 'research-complete',
                        label: 'Research Complete'
                    },
                ]
            }
        },

        mounted() {
            this.projects.selectedProject = this.projectData;
            this.documents.all = this.comp.building.documents;
            this.documents.filtered = this.comp.building.documents;
            this.buildings.all.push(this.compData);
            this.buildings.filtered.push(this.compData);
            this.buildings.selectedBuildingId = this.compData.building_id;
            this.buildings.selectedBuilding = this.compData.building;
            this.units.all = this.compData.building.units;
            this.units.filtered = this.compData.building.units;
            this.ksds.scenarios = this.projectData.ksd_scenarios;
            this.ksds.selectedScenarioId = this.ksds.scenarios[0].id || null;
        },

        computed: {
            enabledTabs() {
                // v-for used to be paired with v-if="tab.enabled"; Vue 2 runs
                // v-for first so the list was already filtered. Same result, and
                // it does not break when the directive priority reverses in Vue 3.
                const source = this.tabs;
                return Array.isArray(source)
                    ? source.filter(entry => entry.enabled)
                    : Object.fromEntries(Object.entries(source || {}).filter(([, entry]) => entry.enabled));
            },

            ...mapState(['projects', 'buildings', 'documents', 'units', 'ksds']),
            ...mapGetters(['scenario', 'scenarioKsds']),

            isNegotiationMode() {
                return this.scenario?.negotiation_mode;
            },

            project() {
                return this.projectData;
            },

            comp() {
                return this.compData;
            },

            tabs() {
                return {
                    score: {
                        label: "Score",
                        enabled: true,
                        showCount: false
                    },
                    building: {
                        label: "Building",
                        enabled: true,
                        showCount: false
                    },
                    units: {
                        label: "Units",
                        enabled: true,
                        showCount: true,
                        count: this.comp.building.units?.length || 0
                    },
                    documents: {
                        label: "Documents",
                        enabled: true,
                        showCount: true,
                        count: this.documents.all.length || 0
                    },
                    contacts: {
                        label: "Contacts",
                        enabled: true,
                        showCount: true,
                        count: this.comp.building.contacts?.length || 0
                    },
                    comments: {
                        label: "Comments",
                        enabled: true,
                        showCount: true,
                        count: this.comp.comments?.length || 0
                    },
                    effective_rent_calculator: {
                        label: "Eff. Rent Calculator",
                        enabled: true,
                        showCount: false
                    },
                    lease_vs_purchase_calculator: {
                        label: "Lease vs. Purchase",
                        enabled: ! this.fromReatlas,
                        showCount: false
                    },
                    changelog: {
                        label: "Changelog",
                        enabled: this.$gate.isAdmin() && ! this.fromReatlas,
                        showCount: true,
                        count: this.comp.audits?.length || 0
                    }
                }
            },

            researchStatusTitle() {
                return this.comp.research_status.replace(/-/g, ' ').replace(/(^\w{1})|(\s+\w{1})/g, letter => letter.toUpperCase());
            }
        },

        methods: {
            showComps() {
                this.$modal.show('comp-table');
            },

            gotoEditBuilding() {
                location.href = `/buildings/${this.comp.building.id}/edit`
            },

            updateComp(params) {
                axios.patch('/projects/' + this.project.id + '/comps/' + this.comp.id, params)
                    .then((response) => {
                        this.$notify({
                            type: 'success',
                            title: 'Success',
                            text: response.data.message
                        }); 
                    }).catch((response) => {
                        this.$notify({
                            type: 'error',
                            title: 'Error',
                            text: response.data.message
                        }); 
                    });
            },

            updateCompScore(score) {
                this.comp.score = score;
                
                this.setScoreColor();

                this.$forceUpdate();
            },

            setScoreColor() {
                this.comp.score_color = (this.comp.score < 70 ? 'bg-red-500 border-red-600' : (this.comp.score >= 90 ? 'bg-green-500 border-green-300' : 'bg-yellow-200 text-yellow-700 border-yellow-300'));
            },

            toggleFavorite() {
                const favorited_at = this.comp.is_favorite ? null : moment().format('Y-MM-D H:m:ss');
                const favorited_by = this.comp.is_favorite ? null : window.user.id;

                this.comp.favorited_at = favorited_at;
                this.comp.favorited_by = favorited_by;
                this.comp.is_favorite = favorited_at ? true : false;

                this.updateComp({
                    favorited_at: favorited_at,
                    favorited_by: favorited_by
                });
            },

            toggleRejected() {
                const rejected_at = this.comp.is_rejected ? null : moment().format('Y-MM-D H:m:ss');
                const rejected_by = this.comp.is_rejected ? null : window.user.id;

                this.comp.rejected_at = rejected_at;
                this.comp.rejected_by = rejected_by;
                this.comp.is_rejected = rejected_at ? true : false;

                this.updateComp({
                    rejected_at: rejected_at,
                    rejected_by: rejected_by
                });
            },

            changeResearchStatus(status) {
                this.comp.research_status = status;

                this.updateComp({
                    research_status: status
                });

                this.$refs.popover.isOpen = false;
            },
        }
    }
</script>